import React from 'react';
import { MobxComponent } from "../../../mobx/components";
import { inject, observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { IconClose, IconArrowForward, IconAlertDanger, IconAlert } from "./icons";
import { MenuItem, QuickSettingsContainer, MenuColorHeaderWrapper, MenuColorHeaderTitleWrapper, MenuColorBodyWrapper, MenuColorFooterWrapper, ButtonV2, MenuStatusAlert, MenuStatusAlertText, MenuStatusParagraph, MenuStatusAssignmentContainer, MenuStatusAssignmentList, MenuStatusLabel, MenuStatusAssignmentTo, MenuStatusShowSettingsContainer, MenuStatusShowSettingsText, MenuBodyFooter, MenuStatusLabelAvailable } from "./layout.c";
import { IconKioskWrapper } from '../../ui/restaurant/views/menus-v2/item-v2';
import { CoreUtils, ListServicesOptionsNoB } from '@lib/common';
import { POS_PRINTERS } from './data';

/** Describes which entity will be updated (menu, category, or item) */
export type OverrideEntityType = 'menu' | 'category' | 'item';

/** What the user is editing when the override prompt appears */
export type OverrideEditKind = 'visibility' | 'color';

export interface OverrideTargetEntity {
    type: OverrideEntityType;
    name: string;
    conditions?: any;
    categoryCount?: number;
    itemCount?: number;
}

/** Source ids to compute counts and services from store */
export interface OverrideSourceIds {
    menuId: string;
    categoryId?: string;
    itemId?: string;
}

export interface OverrideFieldChange {
    from?: string;
    to?: string;
}

export interface OverrideArrayChange {
    from?: string[];
    to?: string[];
}

/** Summary of what will be overridden at the entity being edited */
export interface OverrideChangesSummary {
    /** Display / UI color */
    displayColor?: OverrideFieldChange;
    /** POS (pushpos) color */
    posColor?: OverrideFieldChange;
    /** @deprecated use displayColor — kept for backward compatibility */
    color?: OverrideFieldChange;
    /** Legacy printer wording in UI */
    printers?: OverrideArrayChange;
    posPrinters?: OverrideArrayChange;
    serviceTypes?: OverrideArrayChange;
}

/** One descendant row shown in the modal */
export interface AffectedEntityRow {
    entityType: 'category' | 'item';
    name: string;
    id: string;
    displayColor?: OverrideFieldChange;
    posColor?: OverrideFieldChange;
    posPrinters?: OverrideArrayChange;
    serviceTypes?: OverrideArrayChange;
}

type Step = 1 | 2;

export function getServicesFromAvailability(avail: { conditions?: { pos?: { services?: string[] }; online?: { services?: string[] } } } | null | undefined): string[] {
    if (!avail?.conditions) return [];
    const pos = avail.conditions.pos?.services ?? [];
    const online = avail.conditions.online?.services ?? [];
    return [...new Set([...pos, ...online])];
}

export function getServiceLabels(serviceValues: string[]): string[] {
    return serviceValues
        .map(v => ListServicesOptionsNoB.find(o => o.value === v)?.label)
        .filter(Boolean) as string[];
}

/** Map stored pushpos printer slot values (1–8, etc.) to labels from {@link POS_PRINTERS}. */
/** Show override colors as #hex; pass through non-rgb strings (e.g. "—", named colors). */
export function displayColorAsHex(value: string | undefined | null): string {
    if (value == null || value === '') return '';
    const s = String(value).trim();
    if (s === '—') return '—';
    if (s.startsWith('#')) {
        const body = CoreUtils.colors.rgbToHex(s).replace(/^#/, '');
        return `#${body}`;
    }
    if (/^rgba?\(/i.test(s)) {
        const body = CoreUtils.colors.rgbToHex(s).replace(/^#/, '');
        return `#${body}`;
    }
    return s;
}

export function formatPosPrinterValuesForDisplay(values: readonly any[] | undefined): string[] {
    if (!values?.length) return [];
    return values.map((v) => {
        if (typeof v === 'number' && Number.isFinite(v)) {
            const hit = POS_PRINTERS.find(p => p.value === v);
            if (hit) return hit.label;
        }
        if (typeof v === 'string' && v.trim() !== '') {
            const num = Number(v);
            if (!Number.isNaN(num) && Number.isFinite(num)) {
                const hit = POS_PRINTERS.find(p => p.value === num);
                if (hit) return hit.label;
            }
            const byLabel = POS_PRINTERS.find(p => p.label === v);
            if (byLabel) return byLabel.label;
        }
        return String(v);
    });
}

function getCountsAndServices(
    menusV2: any[],
    overrideSource: OverrideSourceIds | undefined,
    entityType: OverrideEntityType
): { categoryCount: number; itemCount: number; serviceLabels: string[] } {
    const empty = { categoryCount: 0, itemCount: 0, serviceLabels: [] as string[] };
    if (!overrideSource?.menuId || !menusV2?.length) return empty;
    const menu = menusV2.find((m: any) => m.id === overrideSource.menuId);
    if (!menu?.availabilities) return empty;
    const getAvail = (id: string | undefined) => id ? menu.availabilities.find((a: any) => a.id === id) : null;
    const collectServices = (avail: any) => getServiceLabels(getServicesFromAvailability(avail));

    if (entityType === 'menu') {
        const categoryCount = menu.categories?.length ?? 0;
        const itemCount = menu.items?.length ?? 0;
        const serviceSet = new Set<string>();
        const menuAvail = getAvail(menu.availability_id);
        getServicesFromAvailability(menuAvail).forEach(s => serviceSet.add(s));
        (menu.categories ?? []).forEach((cat: any) => {
            getServicesFromAvailability(getAvail(cat.availability_id)).forEach(s => serviceSet.add(s));
        });
        (menu.items ?? []).forEach((it: any) => {
            const aid = it.availability_id ?? (menu.categories?.find((c: any) => c.id === it.category_id) as any)?.availability_id ?? menu.availability_id;
            getServicesFromAvailability(getAvail(aid)).forEach(s => serviceSet.add(s));
        });
        return { categoryCount, itemCount, serviceLabels: getServiceLabels([...serviceSet]) };
    }

    if (entityType === 'category' && overrideSource.categoryId) {
        const category = menu.categories?.find((c: any) => c.id === overrideSource.categoryId);
        if (!category) return empty;
        const items = (menu.items ?? []).filter((i: any) => i.category_id === overrideSource.categoryId);
        const itemCount = items.length;
        const serviceSet = new Set<string>();
        getServicesFromAvailability(getAvail(category.availability_id)).forEach(s => serviceSet.add(s));
        items.forEach((it: any) => {
            const aid = it.availability_id ?? category.availability_id ?? menu.availability_id;
            getServicesFromAvailability(getAvail(aid)).forEach(s => serviceSet.add(s));
        });
        return { categoryCount: 0, itemCount, serviceLabels: getServiceLabels([...serviceSet]) };
    }

    if (entityType === 'item' && overrideSource.categoryId && overrideSource.itemId) {
        const item = menu.items?.find((i: any) => i.id === overrideSource.itemId);
        if (!item) return empty;
        const category = menu.categories?.find((c: any) => c.id === overrideSource.categoryId);
        const aid = item.availability_id ?? (category as any)?.availability_id ?? menu.availability_id;
        const serviceLabels = collectServices(getAvail(aid));
        return { categoryCount: 0, itemCount: 1, serviceLabels };
    }

    return empty;
}

interface Props extends WithTranslation {
    show?: boolean;
    onHide?: () => void;
    onConfirm?: () => void;
    onKeepCurrent?: () => void;
    onShowSettings?: () => void;
    title?: string;
    targetEntity?: OverrideTargetEntity | null;
    overrideSource?: OverrideSourceIds | null;
    changesSummary?: OverrideChangesSummary | null;
    /** visibility = service types focus; color = display color focus */
    editKind?: OverrideEditKind;
    /** Descendants that have settings which may be affected */
    affectedEntities?: AffectedEntityRow[] | null;
}

interface State {
    step: Step;
}

function renderArrowPair(from?: string, to?: string, fromIsColor?: boolean, toIsColor?: boolean) {
    return (
        <MenuStatusAssignmentTo>
            {from != null && from !== '' && (
                <MenuStatusParagraph style={fromIsColor && from !== '—' ? { color: from } : undefined}>{from}</MenuStatusParagraph>
            )}
            {(from != null || to != null) && <IconArrowForward />}
            {to != null && to !== '' && (
                <MenuStatusParagraph style={toIsColor && to !== '—' ? { color: to } : undefined}>{to}</MenuStatusParagraph>
            )}
        </MenuStatusAssignmentTo>
    );
}

/** Swatch uses raw CSS color; labels use {@link displayColorAsHex}. */
function renderColorArrowPair(from?: string, to?: string) {
    const fromLabel = from != null && from !== '' ? displayColorAsHex(from) : undefined;
    const toLabel = to != null && to !== '' ? displayColorAsHex(to) : undefined;
    return (
        <MenuStatusAssignmentTo>
            {fromLabel != null && fromLabel !== '' && (
                <MenuStatusParagraph style={from && from !== '—' ? { color: from } : undefined}>{fromLabel}</MenuStatusParagraph>
            )}
            {(from != null || to != null) && <IconArrowForward />}
            {toLabel != null && toLabel !== '' && (
                <MenuStatusParagraph style={to && to !== '—' ? { color: to } : undefined}>{toLabel}</MenuStatusParagraph>
            )}
        </MenuStatusAssignmentTo>
    );
}

function renderArrowPairLists(from: string[], to: string[]) {
    const fl = from.length ? from.join(', ') : '—';
    const tl = to.length ? to.join(', ') : '—';
    return (
        <MenuStatusAssignmentTo>
            <MenuStatusParagraph>{fl}</MenuStatusParagraph>
            <IconArrowForward />
            <MenuStatusParagraph>{tl}</MenuStatusParagraph>
        </MenuStatusAssignmentTo>
    );
}

function renderPosPrinterArrowPairLists(from: readonly any[] | undefined, to: readonly any[] | undefined) {
    return renderArrowPairLists(
        formatPosPrinterValuesForDisplay(from),
        formatPosPrinterValuesForDisplay(to)
    );
}

@inject("store") @observer
class MenuPreOverrideClass extends MobxComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { step: 1 };
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.show && !prevProps.show) {
            this.setState({ step: 1 });
        }
    }

    render() {
        const { show, onHide, onConfirm, onKeepCurrent, onShowSettings, title, targetEntity, overrideSource, changesSummary, editKind = 'visibility', affectedEntities } = this.props;
        const { step } = this.state;
        const entityLabel = targetEntity
            ? `${targetEntity.type.charAt(0).toUpperCase() + targetEntity.type.slice(1)}: ${targetEntity.name}`
            : step === 1 ? 'this entity' : 'the selected entity';

        const disp = changesSummary?.displayColor ?? changesSummary?.color;
        const hasDisplayColor = disp && (disp.from !== undefined || disp.to !== undefined);
        const hasPosColor = changesSummary?.posColor && (changesSummary.posColor.from !== undefined || changesSummary.posColor.to !== undefined);
        const hasPrinters = changesSummary?.printers && (changesSummary.printers.from?.length || changesSummary.printers.to?.length);
        const hasPosPrinters = changesSummary?.posPrinters && (changesSummary.posPrinters.from?.length || changesSummary.posPrinters.to?.length);
        const hasServiceTypes = changesSummary?.serviceTypes && (changesSummary.serviceTypes.from?.length || changesSummary.serviceTypes.to?.length);

        const menusV2 = (this.injected.store.restaurant?.menus_v2 ?? []) as any[];
        const { categoryCount, itemCount, serviceLabels } = getCountsAndServices(menusV2, overrideSource ?? undefined, targetEntity?.type ?? 'menu');
        const servicesText = serviceLabels.length > 0 ? serviceLabels.join(', ') : '—';
        const countsText = (() => {
            if (targetEntity?.type === 'menu') {
                return categoryCount !== 0 || itemCount !== 0
                    ? `${categoryCount} categor${categoryCount === 1 ? 'y' : 'ies'} and ${itemCount} item${itemCount === 1 ? '' : 's'} under this menu (${servicesText})`
                    : null;
            }
            if (targetEntity?.type === 'category') {
                return itemCount !== 0 ? `${itemCount} item${itemCount === 1 ? '' : 's'} in this category (${servicesText})` : null;
            }
            if (targetEntity?.type === 'item') {
                return `1 item (${servicesText})`;
            }
            return null;
        })();

        const affected = affectedEntities ?? [];
        const scopeHint =
            targetEntity?.type === 'menu'
                ? 'categories and items'
                : targetEntity?.type === 'category'
                    ? 'items in this category'
                    : '';

        const editHint =
            editKind === 'color'
                ? 'Display color is being updated. Descendants may already use their own color, POS color, printer routing, or service-type assignments.'
                : 'Visibility (service types) is being updated. Descendants may already use their own color, POS color, printer routing, or service-type assignments.';

        return (
            <QuickSettingsContainer className={show ? 'active' : ''}>
                <MenuColorHeaderWrapper>
                    <MenuColorHeaderTitleWrapper>
                        {step === 1 ? (title || 'Override warning') : 'Confirm and verify'}
                    </MenuColorHeaderTitleWrapper>
                    <MenuItem onClick={onHide}><IconClose /></MenuItem>
                </MenuColorHeaderWrapper>
                <MenuColorBodyWrapper style={{ minHeight: '50vh', overflowY: 'scroll' }}>
                    {step === 1 && (
                        <>
                            <MenuStatusAlert variant='danger'>
                                <IconKioskWrapper variant='danger'>
                                    <IconAlertDanger />
                                </IconKioskWrapper>
                                <MenuStatusAlertText variant='danger'>Override existing values?</MenuStatusAlertText>
                            </MenuStatusAlert>
                            <MenuStatusParagraph>
                                {editHint} {scopeHint ? `This applies to ${scopeHint}.` : ''}
                            </MenuStatusParagraph>
                            {countsText != null && (
                                <MenuStatusParagraph>
                                    <strong>{countsText}</strong>
                                </MenuStatusParagraph>
                            )}
                            <MenuStatusParagraph>
                                <strong>Override all</strong> applies this change through the list below where relevant. <strong>Keep current</strong> leaves the parent as you saved it and does not change descendants.
                            </MenuStatusParagraph>

                            <MenuStatusAssignmentContainer>
                                {hasDisplayColor && (
                                    <MenuStatusAssignmentList>
                                        <MenuStatusLabel>Display color</MenuStatusLabel>
                                        {renderColorArrowPair(disp!.from, disp!.to)}
                                    </MenuStatusAssignmentList>
                                )}
                                {hasPosColor && (
                                    <MenuStatusAssignmentList>
                                        <MenuStatusLabel>POS color</MenuStatusLabel>
                                        {renderColorArrowPair(changesSummary!.posColor!.from, changesSummary!.posColor!.to)}
                                    </MenuStatusAssignmentList>
                                )}
                                {hasPrinters && (
                                    <MenuStatusAssignmentList>
                                        <MenuStatusLabel>Printer assignment</MenuStatusLabel>
                                        {renderArrowPairLists(changesSummary!.printers!.from ?? [], changesSummary!.printers!.to ?? [])}
                                    </MenuStatusAssignmentList>
                                )}
                                {hasPosPrinters && (
                                    <MenuStatusAssignmentList>
                                        <MenuStatusLabel>POS printer assignment</MenuStatusLabel>
                                        {renderPosPrinterArrowPairLists(changesSummary!.posPrinters!.from, changesSummary!.posPrinters!.to)}
                                    </MenuStatusAssignmentList>
                                )}
                                {hasServiceTypes && (
                                    <MenuStatusAssignmentList>
                                        <MenuStatusLabel>POS / online service types</MenuStatusLabel>
                                        {renderArrowPairLists(changesSummary!.serviceTypes!.from ?? [], changesSummary!.serviceTypes!.to ?? [])}
                                    </MenuStatusAssignmentList>
                                )}
                                {!hasDisplayColor && !hasPosColor && !hasPrinters && !hasPosPrinters && !hasServiceTypes && (
                                    <MenuStatusAssignmentList>
                                        <MenuStatusLabel>Changes</MenuStatusLabel>
                                        <MenuStatusParagraph>Review affected rows below. Confirm to propagate; keep current to update only the parent.</MenuStatusParagraph>
                                    </MenuStatusAssignmentList>
                                )}
                            </MenuStatusAssignmentContainer>

                            {affected.length > 0 && (
                                <>
                                    <MenuStatusLabel style={{ marginTop: 16, marginBottom: 8, display: 'block' }}>Affected {targetEntity?.type === 'menu' ? 'categories & items' : 'items'}</MenuStatusLabel>
                                    {affected.map(row => (
                                        <div
                                            key={`${row.entityType}-${row.id}`}
                                            style={{
                                                border: '1px solid #e8e8e8',
                                                borderRadius: 8,
                                                padding: '10px 12px',
                                                marginBottom: 10,
                                                background: '#fafafa',
                                            }}
                                        >
                                            <MenuStatusParagraph style={{ fontWeight: 600, marginBottom: 8 }}>
                                                {row.entityType === 'category' ? 'Category' : 'Item'}: {row.name}
                                            </MenuStatusParagraph>
                                            <MenuStatusAssignmentContainer style={{ gap: 8 }}>
                                                {row.displayColor && (row.displayColor.from || row.displayColor.to) && (
                                                    <MenuStatusAssignmentList>
                                                        <MenuStatusLabel>Display color</MenuStatusLabel>
                                                        {renderColorArrowPair(row.displayColor.from, row.displayColor.to)}
                                                    </MenuStatusAssignmentList>
                                                )}
                                                {row.posColor && (row.posColor.from || row.posColor.to) && (
                                                    <MenuStatusAssignmentList>
                                                        <MenuStatusLabel>POS color</MenuStatusLabel>
                                                        {renderColorArrowPair(row.posColor.from, row.posColor.to)}
                                                    </MenuStatusAssignmentList>
                                                )}
                                                {row.posPrinters && ((row.posPrinters.from?.length ?? 0) > 0 || (row.posPrinters.to?.length ?? 0) > 0) && (
                                                    <MenuStatusAssignmentList>
                                                        <MenuStatusLabel>POS printers</MenuStatusLabel>
                                                        {renderPosPrinterArrowPairLists(row.posPrinters.from, row.posPrinters.to)}
                                                    </MenuStatusAssignmentList>
                                                )}
                                                {row.serviceTypes && ((row.serviceTypes.from?.length ?? 0) > 0 || (row.serviceTypes.to?.length ?? 0) > 0) && (
                                                    <MenuStatusAssignmentList>
                                                        <MenuStatusLabel>Service types</MenuStatusLabel>
                                                        {renderArrowPairLists(row.serviceTypes.from ?? [], row.serviceTypes.to ?? [])}
                                                    </MenuStatusAssignmentList>
                                                )}
                                            </MenuStatusAssignmentContainer>
                                        </div>
                                    ))}
                                </>
                            )}

                            {onShowSettings && (
                                <MenuStatusShowSettingsContainer onClick={onShowSettings}>
                                    <MenuStatusShowSettingsText style={{ color: '#000' }}>Show settings</MenuStatusShowSettingsText>
                                    <IconArrowForward />
                                </MenuStatusShowSettingsContainer>
                            )}
                            <MenuBodyFooter>
                                <MenuStatusParagraph><IconAlert /> What do these options mean?</MenuStatusParagraph>
                                <ul>
                                    <li><MenuStatusLabel>Override all – Apply the change to descendants in the list (service types and/or display color, depending on what you edited).</MenuStatusLabel></li>
                                    <li><MenuStatusLabel>Keep current – Parent is already updated; leave each descendant’s settings as they are.</MenuStatusLabel></li>
                                </ul>
                            </MenuBodyFooter>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <MenuStatusAlert variant='danger'>
                                <IconKioskWrapper variant='danger'>
                                    <IconAlertDanger />
                                </IconKioskWrapper>
                                <MenuStatusAlertText variant='danger'>Confirm override</MenuStatusAlertText>
                            </MenuStatusAlert>
                            <MenuStatusLabelAvailable>
                                Please verify below. <strong>Override all</strong> will update descendants as shown. This cannot be undone without editing each row again.
                            </MenuStatusLabelAvailable>
                            {countsText != null && (
                                <MenuStatusParagraph style={{ marginBottom: 12 }}>
                                    <strong>{countsText}</strong>
                                </MenuStatusParagraph>
                            )}
                            <MenuStatusAssignmentContainer>
                                <MenuStatusAssignmentList>
                                    <MenuStatusLabel>Target updated</MenuStatusLabel>
                                    <MenuStatusParagraph>{entityLabel}</MenuStatusParagraph>
                                </MenuStatusAssignmentList>
                                {hasDisplayColor && (
                                    <MenuStatusAssignmentList>
                                        <MenuStatusLabel>Display color</MenuStatusLabel>
                                        <MenuStatusParagraph>
                                            {disp!.from != null && disp!.from !== ''
                                                ? `${displayColorAsHex(disp!.from)} → `
                                                : ''}
                                            {disp!.to == null || disp!.to === ''
                                                ? '—'
                                                : displayColorAsHex(disp!.to)}
                                        </MenuStatusParagraph>
                                    </MenuStatusAssignmentList>
                                )}
                                {hasPosColor && (
                                    <MenuStatusAssignmentList>
                                        <MenuStatusLabel>POS color</MenuStatusLabel>
                                        <MenuStatusParagraph>
                                            {changesSummary!.posColor!.from != null && changesSummary!.posColor!.from !== ''
                                                ? `${displayColorAsHex(changesSummary!.posColor!.from)} → `
                                                : ''}
                                            {changesSummary!.posColor!.to == null || changesSummary!.posColor!.to === ''
                                                ? '—'
                                                : displayColorAsHex(changesSummary!.posColor!.to)}
                                        </MenuStatusParagraph>
                                    </MenuStatusAssignmentList>
                                )}
                                {hasPosPrinters && (
                                    <MenuStatusAssignmentList>
                                        <MenuStatusLabel>POS printers</MenuStatusLabel>
                                        <MenuStatusParagraph>
                                            {(() => {
                                                const labs = formatPosPrinterValuesForDisplay(changesSummary!.posPrinters!.to);
                                                return labs.length ? labs.join(', ') : '—';
                                            })()}
                                        </MenuStatusParagraph>
                                    </MenuStatusAssignmentList>
                                )}
                                {hasServiceTypes && (
                                    <MenuStatusAssignmentList>
                                        <MenuStatusLabel>POS / online service types</MenuStatusLabel>
                                        <MenuStatusParagraph>
                                            {(changesSummary!.serviceTypes!.to ?? []).length
                                                ? (changesSummary!.serviceTypes!.to ?? []).join(', ')
                                                : '—'}
                                        </MenuStatusParagraph>
                                    </MenuStatusAssignmentList>
                                )}
                            </MenuStatusAssignmentContainer>

                            {affected.length > 0 && (
                                <>
                                    <MenuStatusLabelAvailable style={{ marginTop: 16, display: 'block' }}>Descendants</MenuStatusLabelAvailable>
                                    {affected.map(row => (
                                        <div
                                            key={`c2-${row.entityType}-${row.id}`}
                                            style={{
                                                border: '1px solid #e8e8e8',
                                                borderRadius: 8,
                                                padding: '10px 12px',
                                                marginTop: 8,
                                                background: '#fafafa',
                                            }}
                                        >
                                            <MenuStatusParagraph style={{ fontWeight: 600 }}>{row.entityType === 'category' ? 'Category' : 'Item'}: {row.name}</MenuStatusParagraph>
                                        </div>
                                    ))}
                                </>
                            )}

                            <MenuStatusLabelAvailable style={{ marginTop: 16 }}>
                                Are you sure you want to apply these overrides?
                            </MenuStatusLabelAvailable>
                        </>
                    )}
                </MenuColorBodyWrapper>
                <MenuColorFooterWrapper>
                    {step === 1 && (
                        <>
                            <ButtonV2 variant="default" onClick={onHide}>Cancel</ButtonV2>
                            <ButtonV2 variant="default" onClick={() => { onKeepCurrent?.(); onHide?.(); }}>Keep current</ButtonV2>
                            <ButtonV2 onClick={() => this.setState({ step: 2 })}>Override all</ButtonV2>
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <ButtonV2 variant="default" onClick={() => this.setState({ step: 1 })}>Back</ButtonV2>
                            <ButtonV2 variant="default" onClick={onHide}>Cancel</ButtonV2>
                            <ButtonV2 onClick={() => { void Promise.resolve(onConfirm?.()); }}>Yes, apply overrides</ButtonV2>
                        </>
                    )}
                </MenuColorFooterWrapper>
            </QuickSettingsContainer>
        );
    }
}
export const MenuPreOverride = withTranslation()(MenuPreOverrideClass);
